import { useCallback, useEffect, useState } from "react";

// Checklist progress is per-shoot and lives only in the browser: it is a memory
// aid for the photographer, not something the backend tracks. Keyed by VIN so a
// photographer working several shoots keeps them separate, and stored as an ISO
// timestamp per item so it doubles as a record of when each step was ticked.
export const storageKeyFor = (vin) => `ect-dispatch-${vin || "unknown"}`;

const read = (key) => {
    try {
        return JSON.parse(window.localStorage.getItem(key)) || {};
    } catch (e) {
        // Corrupt or unavailable storage (Safari private mode throws) must not
        // stop the page from rendering.
        return {};
    }
};

const write = (key, value) => {
    try {
        window.localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
        // Progress is a convenience; losing it is preferable to breaking the page.
    }
};

export const useChecklist = (vin) => {
    const key = storageKeyFor(vin);
    const [checked, setChecked] = useState(() => read(key));

    // Re-read when the shoot changes, so navigating between dispatch pages does
    // not carry one shoot's progress into another.
    useEffect(() => {
        setChecked(read(key));
    }, [key]);

    const toggle = useCallback((itemKey) => {
        setChecked((current) => {
            const next = { ...current };
            if (next[itemKey]) {
                delete next[itemKey];
            } else {
                next[itemKey] = new Date().toISOString();
            }
            write(key, next);
            return next;
        });
    }, [key]);

    const isChecked = useCallback((itemKey) => Boolean(checked[itemKey]), [checked]);

    const countChecked = useCallback(
        (itemKeys) => itemKeys.filter((itemKey) => Boolean(checked[itemKey])).length,
        [checked]
    );

    return { isChecked, toggle, countChecked };
};
